import { motion, AnimatePresence } from "framer-motion";
import { Trophy, X } from "lucide-react";
import { getDynamicIcon } from "@/components/ui/dynamic-icon";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { GoldenParticles } from "./GoldenParticles";
import { useAchievementNotificationSafe } from "@/hooks/useAchievementNotificationSafe";
import { useNotificationSettings } from "@/hooks/useNotificationSettings";
import { useEffect, useState } from "react";

export function AchievementNotification() {
  const navigate = useNavigate();
  const { currentAchievement, isOpen, hideNotification, skipAll, queueLength } = useAchievementNotificationSafe();
  const { config, texts } = useNotificationSettings();

  const handleViewAchievements = () => {
    hideNotification();
    navigate("/meu-armario?tab=achievements");
  };
  const [displayedPoints, setDisplayedPoints] = useState(0);

  // Animated points counter
  useEffect(() => {
    if (!isOpen || !currentAchievement) {
      setDisplayedPoints(0);
      return;
    }

    const target = currentAchievement.points;
    const duration = config.animationDurationMs;
    const steps = 20;
    const increment = target / steps;
    let current = 0;
    
    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        setDisplayedPoints(target);
        clearInterval(timer);
      } else {
        setDisplayedPoints(Math.floor(current));
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [isOpen, currentAchievement, config.animationDurationMs]);

  if (!currentAchievement) return null;

  // Get icon component dynamically
  const IconComponent = getDynamicIcon(currentAchievement.icon, Trophy);

  const isSpecial = currentAchievement.type === "special" || currentAchievement.type === "streak";

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && hideNotification()}>
      <DialogContent 
        className="sm:max-w-md border-none bg-gradient-to-b from-background via-background to-primary/10 overflow-hidden"
        aria-describedby="achievement-description"
      >
        <DialogTitle className="sr-only">{texts.title}</DialogTitle>
        
        {/* Particles */}
        {config.particlesEnabled && (
          <GoldenParticles 
            count={isSpecial && config.showConfettiSpecial ? config.particlesCount * 2 : config.particlesCount} 
            isSpecial={isSpecial}
          />
        )}

        {/* Close button */}
        <button
          onClick={hideNotification}
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          <X className="h-5 w-5" />
          <span className="sr-only">Fechar</span>
        </button>

        <div className="flex flex-col items-center text-center py-6 relative z-10">
          {/* Animated Icon */}
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ 
              type: "spring", 
              stiffness: 200, 
              damping: 15,
              duration: config.animationDurationMs / 1000,
            }}
            className="relative"
          >
            <motion.div
              animate={{ 
                boxShadow: [
                  "0 0 20px hsl(var(--primary) / 0.3)",
                  "0 0 40px hsl(var(--primary) / 0.5)",
                  "0 0 20px hsl(var(--primary) / 0.3)",
                ],
              }}
              transition={{ duration: 2, repeat: Infinity }}
              className="w-24 h-24 rounded-full bg-gradient-to-br from-primary/20 to-primary/40 flex items-center justify-center"
            >
              <IconComponent className="h-12 w-12 text-primary" />
            </motion.div>
          </motion.div>

          {/* Title */}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-2xl font-bold mt-6 text-foreground"
          >
            {texts.title}
          </motion.h2>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-muted-foreground mt-1"
          >
            {texts.subtitle}
          </motion.p>

          {/* Achievement Name */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, type: "spring" }}
            className="mt-6 px-6 py-3 bg-primary/10 rounded-xl border border-primary/20"
          >
            <p className="text-xl font-semibold text-foreground">
              {currentAchievement.name}
            </p>
            {currentAchievement.description && (
              <p id="achievement-description" className="text-sm text-muted-foreground mt-1">
                {currentAchievement.description}
              </p>
            )}
          </motion.div>

          {/* Points */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-4 flex items-center gap-2 text-primary"
          >
            <span className="text-2xl font-bold">+{displayedPoints}</span>
            <span className="text-lg">{texts.pointsSuffix}</span>
            <motion.span
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 1 }}
            >
              ✨
            </motion.span>
          </motion.div>

          {/* Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="mt-6 w-full space-y-2"
          >
            <Button 
              onClick={handleViewAchievements} 
              className="w-full h-12 text-lg"
              autoFocus
            >
              {texts.buttonText}
            </Button>
            
            {/* Skip all button if queue has items */}
            {config.queueShowCount && queueLength > 0 && (
              <Button
                variant="ghost"
                onClick={skipAll}
                className="w-full text-muted-foreground"
              >
                {texts.skipAllText} ({queueLength} restantes)
              </Button>
            )}
          </motion.div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
